import { ref, computed, onMounted, onUnmounted, type InjectionKey } from 'vue';
import { useI18n } from 'vue-i18n';
import type { AxiosInstance } from 'axios';
import { onDataChanged } from '../utils/dataChanged';
import { describeNeedsYouItems, type NeedsYouItemView } from '../utils/needsYouItems';
import { needsYouStripHidden, hideNeedsYouStrip } from '../utils/needsYouHidden';

// The ONE "needs you" strip (issue 713), replacing the legal-notice strip: every
// app shell mounts it in the banner slot beside the billing banner (which it does
// not touch).
//
// user-api decides EVERYTHING this strip shows: `GET /tenant/needs-you` answers
// `{ items: [...] }` with only what is open AND fixable by the caller, and `{ items: [] }`
// for a person with nothing to do. The UI therefore never ORs a client-side role,
// store or demo flag into whether an item shows, and never asks payment-api whether
// billing is complete. Non-critical: a 403, a 5xx or a network failure resolve to
// no strip rather than an error surface.
//
// Staleness, as it was for the legal strip (issues 620, 681): a routed form asks for
// an immediate `fresh` read through `needsYouRefreshKey` (workspace-ui's notice
// forms and BillingView), the same-tab `lumi:data-changed` signal does so after a
// legal-notice write or a statutory-rules confirmation / agreement write, and
// the 60 s poll is the backstop for what this tab did not write itself. Only the
// LATEST read may write state. user-api answers a `fresh` read from its cache while
// that is under ~1 s old, so a fresh read asked inside FRESH_FLOOR_MS of the last
// answer is deferred to the end of the floor (one timer; asks coalesce), not dropped.
const NEEDS_YOU_POLL_MS = 60_000;
// One save is often several requests; a trailing debounce answers them with one read.
const DATA_CHANGED_DEBOUNCE_MS = 400;
// user-api's fresh floor is 1 s (LEGAL_READINESS_FRESH_FLOOR_MS); a little over it.
const FRESH_FLOOR_MS = 1_100;
// Only a write that can change the answer goes through this signal — every other
// write in the tab must not cost user-api a fresh read of several services: a
// legal notice, and the statutory-rules page's confirmation or an agreement (the
// first deviation of its own counts as "the rules were looked at"). The URL is
// relative to the Time client's base, so it starts at `/statutory-rules`.
const NEEDS_YOU_WRITE_URL = /portal-pages|employee-notice|statutory-rules\/(confirm|agreements)/;

/** Provided by the one AppChrome that owns this composable's state, so a routed form below it can ask for an immediate re-check (workspace-ui's notice forms, BillingView). */
export const needsYouRefreshKey: InjectionKey<() => Promise<void>> = Symbol('needsYouRefresh');

export interface NeedsYouStripProps {
  items: NeedsYouItemView[];
  listLabel: string;
  hideLabel: string;
  /** Bound by `v-bind`, Vue registers it as the component's `hide` listener. */
  onHide: () => void;
}

export function useNeedsYouStrip(opts: {
  api: AxiosInstance; // user-api client
  workspaceUrl: string | (() => string);
  canLoad?: () => boolean; // return false to skip (and clear) the fetch
  /** e.g. `{ headers: { 'X-Tenant-Slug': slug } }` — only needed where the client does not already attach it globally. */
  requestConfig?: () => Record<string, unknown>;
  /** Where the billing item leads, resolved by the app (buildWorkspaceBillingHref); '' leaves the item without a link target. */
  billingHref?: () => string;
  /** Who is looking: the hide choice is stamped to this user, per workspace slug. */
  identity?: () => { tenantSlug?: string | null; userId?: string | null };
}) {
  const { t } = useI18n();
  const answer = ref<{ items: unknown[] } | null>(null);
  const hideTick = ref(0); // bumps when the hide choice changes in this tab or another

  let latestRead = 0;
  let lastAnsweredAt = 0; // when the last answer arrived — user-api caches it from then
  let deferred: { timer: ReturnType<typeof setTimeout>; promise: Promise<void>; settle: () => void } | null = null;

  /** `fresh` bypasses user-api's short per-tenant cache — for a read right after something changed. Inside its floor the read waits for the remainder (resolves once that read is done). */
  function loadNeedsYou(readOpts: { fresh?: boolean } = {}): Promise<void> {
    if (!readOpts.fresh) return readNeedsYou(readOpts);
    const wait = lastAnsweredAt ? Math.min(FRESH_FLOOR_MS, FRESH_FLOOR_MS - (Date.now() - lastAnsweredAt)) : 0;
    if (wait <= 0) return readNeedsYou(readOpts);
    if (deferred) return deferred.promise;
    let settle!: () => void;
    const promise = new Promise<void>((resolve) => { settle = resolve; });
    const timer = setTimeout(() => {
      deferred = null;
      void readNeedsYou({ fresh: true }).then(settle);
    }, wait);
    deferred = { timer, promise, settle };
    return promise;
  }

  async function readNeedsYou(readOpts: { fresh?: boolean }): Promise<void> {
    const ticket = ++latestRead;
    if (opts.canLoad && !opts.canLoad()) {
      answer.value = null;
      return;
    }
    try {
      const cfg: Record<string, unknown> = { ...(opts.requestConfig?.() ?? {}) };
      if (readOpts.fresh) cfg.params = { ...((cfg.params as Record<string, unknown> | undefined) ?? {}), fresh: 1 };
      const { data } = await opts.api.get('/tenant/needs-you', cfg as never);
      lastAnsweredAt = Date.now();
      if (ticket !== latestRead) return;
      answer.value = Array.isArray((data as { items?: unknown })?.items) ? (data as { items: unknown[] }) : null;
    } catch {
      if (ticket !== latestRead) return;
      answer.value = null; // non-critical — 503/403/network alike leave the strip hidden
    }
  }

  function hide(): void {
    const who = opts.identity?.();
    hideNeedsYouStrip(who?.tenantSlug, who?.userId);
    hideTick.value++;
  }

  const needsYouStrip = computed<NeedsYouStripProps | null>(() => {
    void hideTick.value;
    const who = opts.identity?.();
    if (needsYouStripHidden(who?.tenantSlug, who?.userId)) return null;
    const wsUrl = typeof opts.workspaceUrl === 'function' ? opts.workspaceUrl() : opts.workspaceUrl;
    const items = describeNeedsYouItems(answer.value?.items, {
      t,
      workspaceUrl: wsUrl || '',
      billingHref: opts.billingHref?.() ?? '',
    });
    if (items.length === 0) return null;
    return { items, listLabel: t('needsYou.label'), hideLabel: t('needsYou.hide'), onHide: hide };
  });

  let pollTimer: ReturnType<typeof setInterval> | null = null;
  let changedTimer: ReturnType<typeof setTimeout> | null = null;
  let stopDataChanged: (() => void) | null = null;
  const onStorage = () => { hideTick.value++; }; // another tab hid it (or a sign-out cleared it)
  onMounted(() => {
    pollTimer = setInterval(() => void loadNeedsYou(), NEEDS_YOU_POLL_MS);
    stopDataChanged = onDataChanged((detail) => {
      if (!NEEDS_YOU_WRITE_URL.test(detail?.url ?? '')) return;
      if (changedTimer) clearTimeout(changedTimer);
      changedTimer = setTimeout(() => {
        changedTimer = null;
        void loadNeedsYou({ fresh: true });
      }, DATA_CHANGED_DEBOUNCE_MS);
    });
    if (typeof window !== 'undefined') window.addEventListener('storage', onStorage);
  });
  onUnmounted(() => {
    if (pollTimer) clearInterval(pollTimer);
    stopDataChanged?.();
    if (changedTimer) clearTimeout(changedTimer);
    if (deferred) { clearTimeout(deferred.timer); deferred.settle(); deferred = null; }
    if (typeof window !== 'undefined') window.removeEventListener('storage', onStorage);
  });

  return { answer, loadNeedsYou, needsYouStrip };
}
